import React, { useEffect, useState } from 'react';
import { compose } from 'redux';

import Form from '../MainForm';
import ErrorMessage from '../ErrorMessage';
import InputText from '../InputText';
import InputTitle from '../InputTitle';
import Select from '../Select';
import AddressAutocomplete from '../AddressAutocomplete';
import AddressIncompleteNotice from '../AddressIncompleteNotice';
import { Row, ThreeRows, TwoRows } from '../../Structure';
import {
  withApplicantActions,
  withScreenActions,
} from '../../../hocs/withActions';
import { withApiData, withApplicantData } from '../../../hocs/withData';
import { dayOfMonth, Months, Years } from '../../../helpers/numberOperations';
import {
  CPCS_NUMBER_PATTERN,
  NIN_PATTERN,
  PHONE_PATTERN,
} from '../../../helpers/inputPatterns';

// Fields rendered by this form; any other server error is shown in the summary.
const FORM_FIELDS = [
  'forename',
  'surname',
  'email',
  'phoneNumber',
  'nin',
  'cpcsCardNumber',
  'addressLine1',
  'addressLine2',
  'addressLine3',
  'addressLine4',
  'homePostcode',
];

const ADDRESS_FIELDS = [
  'addressLine1',
  'addressLine2',
  'addressLine3',
  'addressLine4',
  'homePostcode',
];

const CPCS_NUMBER_HINT =
  'Your NOCN/CPCS number is 8 digits and starts with a 4. For example, 41234567.';
const CPCS_NUMBER_ERROR = 'The NOCN/CPCS number must be 8 digits starting with 4.';
const PHONE_ERROR = 'Enter a phone number with 10 to 15 digits.';

const ApplicantDetailsForm = ({
  ApplicantData = {},
  ApiData = {},
  SaveApplicantForm = () => {},
  EnableSpinner = () => {},
}) => {
  const [values, setValues] = useState(ApplicantData);
  const [showAddressFields, setShowAddressFields] = useState(
    Boolean(ApplicantData.addressLine1)
  );
  const [autoFilledAddress, setAutoFilledAddress] = useState(false);

  const incompleteAddress =
    autoFilledAddress &&
    (!values.addressLine1 ||
      !values.addressLine2 ||
      !values.addressLine3 ||
      !values.homePostcode);
  const [clearedFields, setClearedFields] = useState({});

  const { validationErrors = {} } = ApiData;
  const errorFor = (field) => {
    if (clearedFields[field]) return '';
    return validationErrors[field] || '';
  };

  // Bring the first failing field into view; the form is longer than the screen.
  useEffect(() => {
    if (Object.keys(validationErrors).length === 0) return;
    const firstError = document.querySelector('.FormContainer .error-wrapper');
    if (firstError) firstError.scrollIntoView({ block: 'center' });
  }, [validationErrors]);

  const handleSubmit = (event) => {
    event.preventDefault();
    // A new submit gets a fresh set of server errors.
    setClearedFields({});
    EnableSpinner();
    SaveApplicantForm(values);
  };

  const updateField = (event) => {
    const { name } = event;
    // The card number is digits only; strip anything pasted in around them,
    // then cap at 8. No maxlength attribute: it would cut a pasted
    // "41-23 4567" before the separators are removed.
    const value =
      name === 'cpcsCardNumber'
        ? event.value.replace(/[^0-9]/g, '').slice(0, 8)
        : event.value;
    setValues({ ...values, [name]: value });
    // Hide the server error for a field as soon as the user edits it.
    if (validationErrors[name] && !clearedFields[name]) {
      setClearedFields({ ...clearedFields, [name]: true });
    }
  };

  const handleAddressSelect = (addressFields) => {
    setValues((prevValues) => ({
      ...prevValues,
      ...addressFields,
    }));
    setShowAddressFields(true);
    setAutoFilledAddress(true);
  };

  const handleNoResults = () => {
    setShowAddressFields(true);
    setAutoFilledAddress(false);
  };

  const summaryErrors = Object.entries(validationErrors).filter(
    ([field]) => !FORM_FIELDS.includes(field)
  );

  // The address inputs stay hidden until the lookup runs, so reveal them when
  // the server rejects an address field; otherwise the error is unreachable.
  const hasAddressError = ADDRESS_FIELDS.some((field) => errorFor(field) !== '');

  return (
    <Form onSubmit={handleSubmit}>
      {summaryErrors.map(([field, message]) => (
        <ErrorMessage key={field} error={message} />
      ))}
      <TwoRows>
        <InputText
          name="forename"
          error={errorFor('forename')}
          labelTitle="Forename*"
          placeholder="Type your forename here"
          required
          value={values.forename}
          onChange={updateField}
        />
        <InputText
          name="surname"
          error={errorFor('surname')}
          labelTitle="Surname*"
          placeholder="Type your surname here"
          required
          value={values.surname}
          onChange={updateField}
        />
      </TwoRows>
      <ThreeRows className="not-full">
        <Select
          labelTitle="Date of Birth*"
          name="day"
          options={dayOfMonth}
          required
          value={values.day}
          onChange={updateField}
        />
        <Select
          name="month"
          options={Months}
          required
          value={values.month}
          onChange={updateField}
        />
        <Select
          name="year"
          options={Years}
          required
          value={values.year}
          onChange={updateField}
        />
      </ThreeRows>
      <Row>
        <InputText
          name="email"
          error={errorFor('email')}
          labelTitle="Email*"
          placeholder="Type your email here"
          type="email"
          required
          value={values.email}
          onChange={updateField}
        />
      </Row>
      <Row className="not-full">
        <InputText
          name="phoneNumber"
          error={errorFor('phoneNumber')}
          labelTitle="Telephone Number*"
          placeholder="Type your phone number here"
          type="tel"
          pattern={PHONE_PATTERN}
          title={PHONE_ERROR}
          required
          value={values.phoneNumber}
          onChange={updateField}
        />
      </Row>
      <Row className="not-full">
        <InputText
          labelTitle="National Insurance No.*"
          name="nin"
          error={errorFor('nin')}
          onChange={updateField}
          pattern={NIN_PATTERN}
          placeholder="Type your National Insurance here"
          required
          value={values.nin}
        />
      </Row>
      <Row>
        <InputText
          name="cpcsCardNumber"
          labelTitle="NOCN/CPCS Number*"
          hint={CPCS_NUMBER_HINT}
          placeholder="For example, 41234567"
          required
          type="text"
          inputMode="numeric"
          pattern={CPCS_NUMBER_PATTERN}
          title={CPCS_NUMBER_ERROR}
          error={errorFor('cpcsCardNumber')}
          value={values.cpcsCardNumber}
          onChange={updateField}
        />
      </Row>
      <Row>
        <AddressAutocomplete
          onAddressSelect={handleAddressSelect}
          onNoResults={handleNoResults}
          labelTitle="Search Address"
        />
      </Row>
      {(showAddressFields || hasAddressError) && (
        <>
          <AddressIncompleteNotice show={incompleteAddress} />
          <InputTitle>Card Holders Home Address</InputTitle>
          <Row>
            <InputText
              name="addressLine1"
              error={errorFor('addressLine1')}
              labelTitle="Address Line 1*"
              required
              value={values.addressLine1}
              onChange={updateField}
            />
          </Row>
          <Row>
            <InputText
              name="addressLine2"
              error={errorFor('addressLine2')}
              labelTitle="Address Line 2*"
              required
              value={values.addressLine2}
              onChange={updateField}
            />
          </Row>
          <Row>
            <InputText
              name="addressLine3"
              error={errorFor('addressLine3')}
              labelTitle="Address Line 3*"
              required
              value={values.addressLine3}
              onChange={updateField}
            />
          </Row>
          <Row>
            <InputText
              name="addressLine4"
              error={errorFor('addressLine4')}
              labelTitle="Address Line 4"
              value={values.addressLine4}
              onChange={updateField}
            />
          </Row>
          <Row>
            <InputText
              name="homePostcode"
              error={errorFor('homePostcode')}
              labelTitle="Postcode*"
              required
              value={values.homePostcode}
              onChange={updateField}
            />
          </Row>
        </>
      )}
    </Form>
  );
};

export default compose(
  withApiData,
  withApplicantData,
  withApplicantActions,
  withScreenActions
)(ApplicantDetailsForm);
